import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../chat/api";
import CopilotSidePanel from "./CopilotSidePanel";
import { onDraftEvent } from "../../../services/sectionDraft";

/* The proposal, as it gets written.
 *
 * The conversation says what is happening; this says what exists.
 *
 * IT TYPES. A node being drafted renders from the live delta stream in the
 * place the paragraph will live, so the document fills in rather than appearing.
 * The deltas come off a subscription, not page state — they arrive several
 * times a second and routing them through the transcript would re-render every
 * message for text only this panel shows.
 *
 * MEASURE. Body text is capped at 34rem. The panel is 620px wide and running
 * text to its full width is ~88 characters a line, well past the point where
 * the eye loses the return sweep and re-reads the line it just finished. The
 * slack on the right is not wasted space; it is what makes the column readable.
 *
 * IT FOLLOWS THE TAIL, NOT THE HEADING. Scrolling the heading into view centres
 * it and then lets the text grow off the bottom, which is how the panel ended up
 * opening mid-paragraph. A marker after the live text, kept in view, tracks the
 * writing the way a terminal does — and once nothing is streaming it stops
 * moving, so a reader who scrolls back is left alone.
 *
 * WHAT IS NOT WRITTEN IS STILL SHOWN, IN PLACE. Unwritten sections keep their
 * position and numbering, greyed, with a ruled placeholder where the prose will
 * go. A document that ends abruptly reads as broken; one that shows what is
 * still coming reads as in progress.
 */

const ProposalDocumentPanel = ({
  projectId,
  projectName,
  visible,
  onClose,
  activeIdx = 0,
  step,
  focusSectionId,
  onFocusHandled,
}) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [live, setLive] = useState({});
  const [writingId, setWritingId] = useState(null);
  const tailRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/v1/rfp/folders/${projectId}/section-view/`);
      setDoc({
        name: res?.data?.folder_name || projectName || "Proposal",
        deadline: res?.data?.deadline || "",
        sections: Array.isArray(res?.data?.sections) ? res.data.sections : [],
      });
    } catch {
      setError("Couldn't open the document.");
    }
  }, [projectId, projectName]);

  useEffect(() => {
    load();
  }, [load]);

  /* Re-read at resting points only. Refetching mid-node would replace the
   * paragraph being written with the empty string the server still holds. */
  useEffect(() => {
    if (step === "sectionDone" || step === "complete") load();
  }, [step, activeIdx, load]);

  useEffect(
    () =>
      onDraftEvent(({ type, targetId, delta }) => {
        if (type === "start") {
          setWritingId(targetId);
          setLive((p) => ({ ...p, [targetId]: "" }));
        } else if (type === "delta") {
          setLive((p) => ({ ...p, [targetId]: (p[targetId] || "") + delta }));
        } else if (type === "done") {
          setWritingId((w) => (w === targetId ? null : w));
          // Dropped a beat late so the text does not blink out before the
          // refetch that replaces it lands.
          setTimeout(
            () =>
              setLive((p) => {
                const next = { ...p };
                delete next[targetId];
                return next;
              }),
            1200
          );
        }
      }),
    []
  );

  useEffect(() => {
    if (!writingId) return;
    tailRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [writingId, live]);

  /* Take the reader to a section they asked for from the transcript.
   *
   * Waits for the document: the panel is usually opened by the same click, so
   * the first render has no sections to scroll to. Cleared through the callback
   * once done, otherwise re-opening the same row a second time would be a
   * no-op — the id would not have changed. */
  useEffect(() => {
    if (!focusSectionId || !doc) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(`sensa-sec-${focusSectionId}`);
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
      onFocusHandled?.();
    }, 90);
    return () => clearTimeout(t);
  }, [focusSectionId, doc, onFocusHandled]);

  const liveText = (id) => (live[id] !== undefined && live[id] !== "" ? live[id] : null);
  const written = (n) => !!liveText(n.id) || !!String(n.response || "").trim();

  /* One type scale for prose, whether it is arriving live or already stored -
   * the two render through different elements, and any drift between them shows
   * up as text that visibly resizes the moment a section finishes. */
  // max-w is load-bearing and was lost when this constant was extracted: without
  // it the column runs the full width of the sheet, about ninety characters a
  // line, well past where the eye keeps its place on the return sweep.
  /* Serif, and a size that assumes reading rather than scanning.
   *
   * 13.5px sans at 1.75 is an interface describing a document. 15px serif at
   * 1.7 is the document — the same words, read rather than surveyed. The ink is
   * warmer and darker than the old #42404A too: mid-grey body copy is a UI
   * convention that makes long prose look provisional. */
  const PROSE = "font-doc max-w-[34rem] text-[15px] leading-[1.7] text-[#2B2731]";

  const body = (node) => {
    const streaming = liveText(node.id);
    if (streaming) {
      return (
        <p className={`whitespace-pre-wrap ${PROSE}`}>
          {streaming}
          <span
            ref={tailRef}
            className="ml-[1px] inline-block h-[1.05em] w-[2px] translate-y-[3px] animate-pulse bg-[#8E4187] align-middle motion-reduce:animate-none"
          />
        </p>
      );
    }
    const stored = String(node.response || "");
    if (!stored.trim()) return null;

    /* Most drafted sections come back as PLAIN TEXT, not markup.
     *
     * Pushing that through dangerouslySetInnerHTML rendered it correctly as
     * HTML — which is to say every newline collapsed to a single space and a
     * six-paragraph section arrived as one unbroken slab. The paragraphs were
     * in the data the whole time; the renderer was throwing them away.
     *
     * So: markup is rendered as markup, and text is split on blank lines and
     * given real paragraphs. Detected rather than configured, because the
     * pipeline emits both and neither the panel nor the row knows which. */
    if (/<[a-z][\s\S]*?>/i.test(stored)) {
      return (
        <div
          /* Server-generated markup from our own drafting pipeline - the same
             content the project screen renders. Not user-authored HTML. */
          className={`sensa-doc ${PROSE}`}
          dangerouslySetInnerHTML={{ __html: stored }}
        />
      );
    }
    return (
      <div className={PROSE}>
        {stored
          .split(/\n\s*\n/)
          .map((para) => para.trim())
          .filter(Boolean)
          .map((para, i) => (
            // Single newlines inside a paragraph are soft wraps in the source,
            // not breaks the author meant — collapsed, as prose.
            <p key={i} className={i ? "mt-[0.85em]" : ""}>
              {para.replace(/\s*\n\s*/g, " ")}
            </p>
          ))}
      </div>
    );
  };

  const sections = doc?.sections || [];
  const done = sections.filter((s) => written(s) || (s.subsections || []).some(written));
  const pct = sections.length ? (done.length / sections.length) * 100 : 0;

  return (
    <CopilotSidePanel visible={visible} label="Proposal document" onClose={onClose} width={620}>
      {/* The document's NAME, at the top, where the name of the thing you are
          looking at belongs.
       *
       * It used to sit on the sheet as a title page — correct for a printed
       * document, wasteful in a 620px panel: a cover block cost most of the
       * first screen, so the reader's first view of a proposal was its title
       * and two lines of prose. The name is chrome here, not content; the sheet
       * starts at section one.
       *
       * Progress moved out of this bar to the foot of the page, where it can
       * float over the text instead of taking a row from it. */}
      <header className="flex flex-none items-baseline gap-2.5 border-b border-[#18101C]/[0.07] bg-[#FCFBFC] px-5 py-2.5">
        <h2 className="flex-none truncate text-[13.5px] font-semibold tracking-[-0.01em] text-[#18101C]">
          {doc?.name || projectName || "Proposal"}
        </h2>
        <p className="min-w-0 flex-1 truncate text-[11.5px] text-[#8B8492]">
          {sections.length} section{sections.length === 1 ? "" : "s"}
          {doc?.deadline ? (
            <>
              <span className="mx-1.5 text-[#CFC9D4]">·</span>
              due {doc.deadline}
            </>
          ) : null}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close document"
          className="-mr-1 flex h-6 w-6 flex-none items-center justify-center rounded-md text-[#77707E] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>

      {/* The page IS the panel now.
       *
       * It used to be a white sheet floating on a tinted ground — paper on a
       * desk. That reads well and costs 96px of width to the ground's padding
       * and the sheet's shadow, in a column that has 620px to give. The panel
       * already has a border and a header separating it from the conversation,
       * so the desk was a second separator doing the same job for a price.
       *
       * `relative` anchors the progress pill at the foot. */}
      <div className="relative min-h-0 flex-1">
      <div className="h-full overflow-y-auto sensa-scroll bg-white px-8 pb-16 pt-7">
        {error ? (
          <p role="alert" className="text-[13px] text-[#B91C1C]">
            {error}
          </p>
        ) : !doc ? (
          /* Shaped like the first section, so nothing jumps when it lands. */
          <div aria-hidden="true">
            <div className="h-4 w-[42%] animate-pulse rounded bg-[#18101C]/[0.06]" />
            <div className="mt-5 space-y-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-3 animate-pulse rounded bg-[#18101C]/[0.045]"
                  style={{ width: `${[100, 96, 99, 92, 97, 61][i]}%` }}
                />
              ))}
            </div>
          </div>
        ) : (
          <article>
            {/* EVERY section, in document order - written or not.

                The unwritten ones used to be a bulleted list stapled to the end
                under "Still to write", which is a table of contents pretending
                to be part of a document. A real document keeps its numbering
                continuous: you read 1, 2, 3 in full and see 4 onwards waiting
                in place, pale, in the position they will occupy. Same
                information, and it reads as one artefact instead of two. */}
            {sections.map((s) => {
              const isWritten = written(s) || (s.subsections || []).some(written);
              return (
                /* NUMBERS HANG IN THE MARGIN.
                 *
                 * They used to sit inline, so every title started at a
                 * different x depending on how wide its number was — "1." and
                 * "10." pushing their headings apart, and body copy aligned to
                 * neither. Pulling the number into a fixed gutter to the left of
                 * the text block gives one left edge down the whole document
                 * and turns the numbering into apparatus rather than content.
                 *
                 * And they are GREY. Brand colour on structural numbering is
                 * the strongest template signal there is: no real proposal
                 * tints its clause numbers. Colour is worth reserving for the
                 * one thing here that is genuinely live — the writing caret. */
                <section
                  key={s.id}
                  id={`sensa-sec-${s.id}`}
                  // scroll-mt clears the sticky header when jumped to from the
                  // transcript, so the heading is not tucked under the bar.
                  className={`scroll-mt-4 ${isWritten ? "relative mb-10 pl-9 last:mb-0" : "relative mb-6 pl-9 last:mb-0"}`}
                >
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-0 w-7 text-right text-[13px] tabular-nums ${
                      isWritten ? "text-[#A9A2B0]" : "text-[#CFC9D4]"
                    }`}
                    style={{ lineHeight: isWritten ? "1.45" : "1.5" }}
                  >
                    {String(s.number || "").replace(/\.0$/, "")}
                  </span>
                  <h3
                    className={`leading-[1.45] tracking-[-0.012em] ${
                      isWritten
                        ? "mb-3.5 text-[16px] font-semibold text-[#18101C]"
                        : "text-[15px] font-medium text-[#B0A9B7]"
                    }`}
                  >
                    {s.title}
                  </h3>

                  {isWritten ? (
                    <>
                      {body(s)}
                      {(s.subsections || []).filter(written).map((c) => (
                        <div key={c.id} className="relative mt-7">
                          <span
                            aria-hidden="true"
                            className="absolute -left-9 top-0 w-7 text-right text-[12px] leading-[1.5] tabular-nums text-[#BFB8C6]"
                          >
                            {c.number}
                          </span>
                          <h4 className="mb-2 text-[13.5px] font-semibold leading-[1.5] tracking-[-0.008em] text-[#3A3440]">
                            {c.title}
                          </h4>
                          {body(c)}
                        </div>
                      ))}
                    </>
                  ) : (
                    // A ruled placeholder rather than the words "not written".
                    // The blank space IS the message, and it looks like the page
                    // it will become. Aligned to the text column now that the
                    // number has moved out of it.
                    <div className="mt-3 max-w-[34rem] space-y-2.5" aria-hidden="true">
                      <div className="h-px w-full bg-[#18101C]/[0.055]" />
                      <div className="h-px w-[92%] bg-[#18101C]/[0.055]" />
                      <div className="h-px w-[64%] bg-[#18101C]/[0.055]" />
                    </div>
                  )}
                </section>
              );
            })}

            {!sections.length && (
              <p className="text-[13px] text-[#6B6472]">
                Nothing is written yet. The outline appears here as it fills in.
              </p>
            )}
          </article>
        )}
      </div>

      {/* Progress, floating over the foot of the page.
       *
       * In the header it occupied a whole row of a 620px column for two numbers
       * and a hairline. Here it costs nothing: absolutely positioned, so the
       * text runs full height behind it, and the page carries bottom padding so
       * the last paragraph is never hidden underneath.
       *
       * Translucent with a blur rather than solid — over a wall of text a solid
       * chip reads as a hole punched in the page, while a blurred one reads as
       * glass laid on top. pointer-events-none so it can never intercept a
       * click meant for the words behind it. */}
      {!!sections.length && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-3.5">
          <div className="flex items-center gap-2.5 rounded-full border border-[#18101C]/[0.08] bg-white/85 px-3 py-1.5 shadow-[0_2px_10px_-4px_rgba(24,16,28,0.22)] backdrop-blur-sm">
            <div className="h-[3px] w-24 overflow-hidden rounded-full bg-[#18101C]/[0.1]">
              <div
                className="h-full rounded-full bg-[#8E4187] transition-[width] duration-700 ease-sidebar motion-reduce:transition-none"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-medium tabular-nums text-[#6B6472]">
              {done.length}<span className="text-[#B5AEBB]"> / </span>{sections.length}
            </span>
          </div>
        </div>
      )}
      </div>
    </CopilotSidePanel>
  );
};

export default ProposalDocumentPanel;
